import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowCounterClockwise,
  ArrowsClockwise,
  CloudArrowDown,
  CheckCircle,
  DownloadSimple,
  PaperPlaneTilt,
  PencilSimple,
  Trash,
  XCircle,
} from 'phosphor-react';
import { AcaoLote, IApuracaoSn, ICorrecao, ILinha, IPendencia, mensagemErro, snApi } from './api';
import {
  EDITAVEL,
  FONTES_RBT12,
  SITUACOES,
  TIPOS_ARQUIVO,
  TRIBUTOS,
  brl,
  cnpjFmt,
  dataBr,
  dataHoraBr,
  pct,
} from './regras';
import { ParametrosResumo } from './ApuracaoCard';

const NIVEL: Record<IPendencia['nivel'], { rotulo: string; cor: string }> = {
  bloqueio: { rotulo: 'Bloqueio', cor: 'red' },
  atencao: { rotulo: 'Atenção', cor: 'orange' },
  info: { rotulo: 'Info', cor: 'blue' },
};

function Linha({ r, v, forte }: { r: string; v: React.ReactNode; forte?: boolean }) {
  return (
    <Flex justify="space-between" gap={3} py="4px" fontSize="13px">
      <Text color="#64748B">{r}</Text>
      <Text color="#0F172A" fontWeight={forte ? 800 : 600} textAlign="right">
        {v}
      </Text>
    </Flex>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Box bg="white" border="1px solid #E2E8F0" borderRadius="14px" p="12px 14px">
      <Text fontSize="10.5px" fontWeight={700} color="#94A3B8" letterSpacing="0.12em" mb={2}>
        {titulo.toUpperCase()}
      </Text>
      {children}
    </Box>
  );
}

const CORRECAO_VAZIA: ICorrecao = {
  documento: '',
  ignorar: false,
  tipo: null,
  icms_st: null,
  monofasico: null,
  anexo: null,
  fator_r: null,
  iss_retido: null,
  motivo: '',
};

/** null = "não mexe" (fica o que a classificação automática decidiu). */
function SimNao({
  rotulo,
  valor,
  onChange,
}: {
  rotulo: string;
  valor: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  return (
    <FormControl>
      <FormLabel fontSize="12px" mb={1}>
        {rotulo}
      </FormLabel>
      <Select
        size="sm"
        value={valor === null ? '' : String(valor)}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value === 'true')}
      >
        <option value="">Automático</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </Select>
    </FormControl>
  );
}

function RetificarModal({
  ap,
  cnpj,
  onFechar,
  onFeito,
}: {
  ap: IApuracaoSn;
  cnpj: string;
  onFechar: () => void;
  onFeito: () => void;
}) {
  const [digitado, setDigitado] = useState('');
  const confere = digitado.replace(/\D/g, '') === (cnpj ?? '').replace(/\D/g, '');
  const retificar = useMutation({
    mutationFn: () => snApi.retificar(ap.id, digitado),
    onSuccess: r => {
      if (r.ok) toast.success('Retificadora transmitida. Gere o DAS novo se houver diferença.');
      else toast.error(r.erro ?? 'O SERPRO recusou a retificadora.');
      onFeito();
    },
    onError: (e: any) => {
      toast.error(mensagemErro(e));
      onFeito();
    },
  });
  return (
    <Modal isOpen onClose={onFechar} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">Retificar declaração</ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize="13px">
          <Text mb={2}>
            Substitui a declaração <b>{ap.numero_declaracao}</b> deste mês no PGDAS-D por uma
            retificadora com o valor aprovado aqui.
          </Text>
          <SimpleGrid columns={2} spacing={2} mb={3}>
            <Text color="#64748B">DAS aprovado</Text>
            <Text fontWeight={700}>{brl(ap.valor_das)}</Text>
            <Text color="#64748B">Receita (rascunho)</Text>
            <Text fontWeight={700}>{ap.valor_serpro != null ? brl(ap.valor_serpro) : '—'}</Text>
          </SimpleGrid>
          <Text mb={3} color="#B45309">
            Se a guia da original já foi paga, o DAS novo sai só com a diferença. Confira antes: não
            dá para desfazer.
          </Text>
          <FormControl>
            <FormLabel fontSize="12px">
              Digite o CNPJ da empresa ({cnpjFmt(cnpj)}) para confirmar
            </FormLabel>
            <Input size="sm" value={digitado} onChange={e => setDigitado(e.target.value)} />
          </FormControl>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="orange"
            isDisabled={!confere}
            isLoading={retificar.isPending}
            onClick={() => retificar.mutate()}
          >
            Transmitir retificadora
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function CorrecaoModal({
  linha,
  atual,
  onFechar,
  onSalvar,
  salvando,
}: {
  linha: ILinha;
  atual?: ICorrecao;
  onFechar: () => void;
  onSalvar: (c: ICorrecao) => void;
  salvando: boolean;
}) {
  const produto = linha.anexo <= 2;
  const [c, setC] = useState<ICorrecao>(atual ?? { ...CORRECAO_VAZIA, documento: linha.documento });
  const set = (p: Partial<ICorrecao>) => setC(v => ({ ...v, ...p }));
  return (
    <Modal isOpen onClose={onFechar} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">Corrigir segregação</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="12px" color="#64748B" mb={3}>
            {linha.documento} · {brl(linha.valor)} · Anexo {linha.anexo}
          </Text>
          <Checkbox isChecked={c.ignorar} onChange={e => set({ ignorar: e.target.checked })} mb={3}>
            Não é receita do Simples (ignorar este documento)
          </Checkbox>
          {!c.ignorar && (
            <SimpleGrid columns={2} spacing={3}>
              {produto ? (
                <>
                  <FormControl>
                    <FormLabel fontSize="12px" mb={1}>
                      Tipo
                    </FormLabel>
                    <Select
                      size="sm"
                      value={c.tipo ?? ''}
                      onChange={e => set({ tipo: (e.target.value || null) as ICorrecao['tipo'] })}
                    >
                      <option value="">Automático</option>
                      <option value="revenda">Revenda (Anexo I)</option>
                      <option value="industrializacao">Industrialização (Anexo II)</option>
                    </Select>
                  </FormControl>
                  <SimNao
                    rotulo="ICMS por ST"
                    valor={c.icms_st}
                    onChange={v => set({ icms_st: v })}
                  />
                  <SimNao
                    rotulo="PIS/COFINS monofásico"
                    valor={c.monofasico}
                    onChange={v => set({ monofasico: v })}
                  />
                </>
              ) : (
                <>
                  <FormControl>
                    <FormLabel fontSize="12px" mb={1}>
                      Anexo
                    </FormLabel>
                    <Select
                      size="sm"
                      value={c.anexo ?? ''}
                      onChange={e =>
                        set({
                          anexo: e.target.value ? (Number(e.target.value) as 3 | 4 | 5) : null,
                        })
                      }
                    >
                      <option value="">Automático</option>
                      <option value="3">Anexo III</option>
                      <option value="4">Anexo IV</option>
                      <option value="5">Anexo V</option>
                    </Select>
                  </FormControl>
                  <SimNao
                    rotulo="Sujeito ao Fator R"
                    valor={c.fator_r}
                    onChange={v => set({ fator_r: v })}
                  />
                  <SimNao
                    rotulo="ISS retido"
                    valor={c.iss_retido}
                    onChange={v => set({ iss_retido: v })}
                  />
                </>
              )}
            </SimpleGrid>
          )}
          <FormControl mt={3} isRequired>
            <FormLabel fontSize="12px" mb={1}>
              Motivo (fica registrado)
            </FormLabel>
            <Textarea size="sm" value={c.motivo} onChange={e => set({ motivo: e.target.value })} />
          </FormControl>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={c.motivo.trim().length < 3}
            isLoading={salvando}
            onClick={() => onSalvar(c)}
          >
            Salvar e recalcular
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function ApuracaoDrawer({
  empresaId,
  competencia,
  onFechar,
  onLote,
  podeTransmitir,
}: {
  empresaId: number | null;
  competencia: string;
  onFechar: () => void;
  onLote: (acao: AcaoLote, ids: number[]) => void;
  podeTransmitir: boolean;
}) {
  const qc = useQueryClient();
  const chave = ['sn-detalhe', empresaId, competencia];
  const { data, isLoading } = useQuery({
    queryKey: chave,
    queryFn: () => snApi.detalhe(empresaId as number, competencia),
    enabled: !!empresaId,
  });
  const [justificativas, setJustificativas] = useState<Record<number, string>>({});
  const [corrigindo, setCorrigindo] = useState<ILinha | null>(null);
  const [filtroDoc, setFiltroDoc] = useState('');
  const [retificando, setRetificando] = useState(false);

  const atualizar = () => {
    qc.invalidateQueries({ queryKey: chave });
    qc.invalidateQueries({ queryKey: ['sn-lista'] });
    qc.invalidateQueries({ queryKey: ['sn-totais'] });
  };
  const opcoes = (ok: string) => ({
    onSuccess: () => {
      toast.success(ok);
      atualizar();
    },
    onError: (e: any) => {
      toast.error(mensagemErro(e));
      atualizar();
    },
  });

  const ap = data?.apuracao;
  const calcular = useMutation({
    mutationFn: () => snApi.calcular(empresaId as number, competencia),
    ...opcoes('Recalculado.'),
  });
  const aprovar = useMutation({
    mutationFn: () => snApi.aprovar(ap!.id),
    ...opcoes('Apuração aprovada.'),
  });
  const desaprovar = useMutation({
    mutationFn: () => snApi.desaprovar(ap!.id),
    ...opcoes('Aprovação desfeita.'),
  });
  const pendencia = useMutation({
    mutationFn: (v: { id: number; acao: 'aceitar' | 'reabrir' }) =>
      snApi.pendencia(v.id, v.acao, justificativas[v.id]),
    ...opcoes('Pendência atualizada.'),
  });
  const salvarCorrecao = useMutation({
    mutationFn: (c: ICorrecao) =>
      snApi.salvarCorrecao({ ...c, empresa_id: empresaId as number, competencia }),
    onSuccess: () => {
      toast.success('Correção salva e mês recalculado.');
      setCorrigindo(null);
      atualizar();
    },
    onError: (e: any) => toast.error(mensagemErro(e)),
  });
  const excluirCorrecao = useMutation({
    mutationFn: (id: number) => snApi.excluirCorrecao(id),
    ...opcoes('Correção removida.'),
  });
  const reabrir = useMutation({
    mutationFn: () => snApi.reabrir(ap!.id),
    ...opcoes('Reaberta: recalcule, confira, aprove e retifique.'),
  });
  const baixar = useMutation({
    mutationFn: (id: number) => snApi.urlArquivo(id),
    onSuccess: r => window.open(r.url, '_blank', 'noopener'),
    onError: (e: any) => toast.error(mensagemErro(e)),
  });

  const correcaoPorDoc = useMemo(
    () => Object.fromEntries((data?.correcoes ?? []).map(c => [c.documento, c])),
    [data?.correcoes],
  );
  const linhas = useMemo(() => {
    const t = filtroDoc.trim().toLowerCase();
    const todas = data?.detalhe?.linhas ?? [];
    return t ? todas.filter(l => l.documento.toLowerCase().includes(t)) : todas;
  }, [data?.detalhe?.linhas, filtroDoc]);

  const editavel = ap ? EDITAVEL.includes(ap.situacao) : false;
  const abertas = (data?.pendencias ?? []).filter(p => p.situacao === 'aberto');
  const s = ap ? SITUACOES[ap.situacao] : SITUACOES.nao_calculada;
  const det = data?.detalhe;

  return (
    <Drawer isOpen={!!empresaId} onClose={onFechar} size="xl" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px solid #E2E8F0" pb={3}>
          <Text fontSize="17px" fontWeight={700} pr={8} noOfLines={1}>
            {data?.empresa.nome ?? 'Carregando…'}
          </Text>
          <Flex gap={2} mt={1} align="center" wrap="wrap">
            <Badge bg={`${s.cor}1A`} color={s.cor}>
              {s.rotulo}
            </Badge>
            <Text fontSize="12px" color="#64748B" fontWeight={400}>
              Competência {dataBr(competencia).slice(3)}
              {ap?.calculado_em ? ` · calculado ${dataHoraBr(ap.calculado_em)}` : ''}
              {ap?.aprovado_por_nome ? ` · aprovado por ${ap.aprovado_por_nome}` : ''}
            </Text>
            {ap?.declarada_por_fora && (
              <Tooltip
                label="Este mês já tem declaração no PGDAS-D (feita fora daqui). Transmitir não envia nada: para mudar o valor, Retificar."
                fontSize="11px"
              >
                <Badge colorScheme="purple">Declarada na Receita nº {ap.numero_declaracao}</Badge>
              </Tooltip>
            )}
          </Flex>
          <Flex gap={2} mt={3} wrap="wrap">
            <Button
              size="sm"
              variant="outline"
              leftIcon={<ArrowsClockwise size={15} />}
              isLoading={calcular.isPending}
              onClick={() => calcular.mutate()}
              isDisabled={!empresaId}
            >
              Recalcular
            </Button>
            {ap && editavel && data?.pode_aprovar && (
              <Tooltip
                label={abertas.length ? 'Trate todas as pendências antes de aprovar' : ''}
                isDisabled={!abertas.length}
                fontSize="11px"
              >
                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<CheckCircle size={15} />}
                  isLoading={aprovar.isPending}
                  isDisabled={abertas.length > 0}
                  onClick={() => aprovar.mutate()}
                >
                  Aprovar
                </Button>
              </Tooltip>
            )}
            {ap?.situacao === 'aprovada' && data?.pode_aprovar && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                leftIcon={<XCircle size={15} />}
                isLoading={desaprovar.isPending}
                onClick={() => desaprovar.mutate()}
              >
                Desfazer aprovação
              </Button>
            )}
            {ap && editavel && (
              <Tooltip
                label="Manda ao SERPRO sem transmitir: a Receita devolve o valor dela para comparar e o RBT12 dela"
                fontSize="11px"
              >
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onLote('rascunho', [empresaId as number])}
                >
                  Rascunho SERPRO
                </Button>
              </Tooltip>
            )}
            {ap && ap.situacao !== 'transmitindo' && (
              <Tooltip
                label="Só consulta: lê a declaração do mês anterior na Receita (apurando 09, lê a de 08) e grava a receita dos 12 meses do RBT12. Não transmite"
                fontSize="11px"
              >
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<CloudArrowDown size={15} />}
                  onClick={() => onLote('importar', [empresaId as number])}
                >
                  Importar da Receita
                </Button>
              </Tooltip>
            )}
            {podeTransmitir && ap?.situacao === 'aprovada' && !ap.numero_declaracao && (
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<PaperPlaneTilt size={15} />}
                onClick={() => onLote('transmitir', [empresaId as number])}
              >
                Transmitir
              </Button>
            )}
            {podeTransmitir && ap?.situacao === 'aprovada' && !!ap.numero_declaracao && (
              <Button
                size="sm"
                colorScheme="orange"
                leftIcon={<PaperPlaneTilt size={15} />}
                onClick={() => setRetificando(true)}
              >
                Retificar
              </Button>
            )}
            {podeTransmitir && (ap?.situacao === 'transmitida' || ap?.situacao === 'concluida') && (
              <Tooltip
                label="Volta a apuração para calculada (guarda a declaração): recalcule, aprove e retifique"
                fontSize="11px"
              >
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  leftIcon={<ArrowCounterClockwise size={15} />}
                  isLoading={reabrir.isPending}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm('Reabrir para retificar? Nada é enviado agora.'))
                      reabrir.mutate();
                  }}
                >
                  Reabrir para retificar
                </Button>
              </Tooltip>
            )}
            {podeTransmitir && ap?.situacao === 'transmitida' && (
              <Button
                size="sm"
                colorScheme="teal"
                onClick={() => onLote('documentos', [empresaId as number])}
              >
                Gerar DAS e extrato
              </Button>
            )}
          </Flex>
        </DrawerHeader>
        <DrawerBody bg="#F8FAFC" pt={4}>
          {isLoading && (
            <Flex justify="center" py={10}>
              <Spinner color="#1B88F5" />
            </Flex>
          )}
          {data && !ap && (
            <Text fontSize="13px" color="#64748B">
              Ainda não calculada neste mês. Clique em Recalcular.
            </Text>
          )}
          {data && ap && (
            <Tabs size="sm" variant="soft-rounded" colorScheme="blue" isLazy>
              <TabList flexWrap="wrap" gap={1} mb={3}>
                <Tab>Resumo</Tab>
                <Tab>
                  Auditoria
                  {abertas.length > 0 && (
                    <Badge ml={2} colorScheme={ap.bloqueios_abertos ? 'red' : 'orange'}>
                      {abertas.length}
                    </Badge>
                  )}
                </Tab>
                <Tab>Documentos ({det?.linhas?.length ?? 0})</Tab>
                <Tab>Arquivos ({data.arquivos.length})</Tab>
              </TabList>
              <TabPanels>
                <TabPanel p={0}>
                  <Flex direction="column" gap={3}>
                    {ap.erro && (
                      <Box
                        bg="red.50"
                        border="1px solid"
                        borderColor="red.200"
                        borderRadius="12px"
                        p={3}
                      >
                        <Text fontSize="13px" color="red.700">
                          {ap.erro}
                        </Text>
                      </Box>
                    )}
                    {det?.recalculo && (
                      <Box
                        bg="orange.50"
                        border="1px solid"
                        borderColor="orange.200"
                        borderRadius="12px"
                        p={3}
                      >
                        <Text fontSize="13px" color="orange.800">
                          Recalculado em {dataHoraBr(det.recalculo.em)} depois da transmissão: hoje
                          daria {brl(det.recalculo.valor_das)} sobre receita de{' '}
                          {brl(det.recalculo.receita)}.
                        </Text>
                      </Box>
                    )}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      <Secao titulo="Valores">
                        <Linha r="Receita bruta do mês" v={brl(ap.receita_bruta)} />
                        {!!ap.receita_externa && (
                          <Linha r="Receita mercado externo" v={brl(ap.receita_externa)} />
                        )}
                        {!!det?.devolucoes && (
                          <Linha r="Devoluções deduzidas" v={brl(det.devolucoes)} />
                        )}
                        <Linha r="DAS (nosso cálculo)" v={brl(ap.valor_das)} forte />
                        <Linha r="Domínio (gabarito)" v={brl(ap.valor_dominio)} />
                        <Linha r="SERPRO (rascunho)" v={brl(ap.valor_serpro)} />
                        <Linha r="Mês anterior" v={brl(ap.valor_mes_anterior)} />
                        {ap.numero_declaracao && <Linha r="Declaração" v={ap.numero_declaracao} />}
                        {ap.numero_das && (
                          <Linha
                            r={`DAS ${ap.numero_das}`}
                            v={`${brl(ap.das_total)} · vence ${dataBr(ap.das_vencimento)}`}
                          />
                        )}
                      </Secao>
                      <Secao titulo="RBT12 e Fator R">
                        <Linha r="RBT12 usado" v={brl(ap.rbt12)} forte />
                        <Linha
                          r="Fonte"
                          v={FONTES_RBT12[ap.rbt12_fonte ?? ''] ?? ap.rbt12_fonte ?? '—'}
                        />
                        <Linha r="Nosso (notas 12 meses)" v={brl(ap.rbt12_proprio)} />
                        <Linha r="Receita Federal" v={brl(ap.rbt12_receita)} />
                        <Linha r="Folha 12 meses" v={brl(ap.folha12)} />
                        <Linha
                          r="Fator R"
                          v={`${pct(ap.fator_r)}${det?.fator_r?.atinge_28 ? ' (≥ 28%: Anexo III)' : ''}`}
                        />
                      </Secao>
                    </SimpleGrid>
                    <Secao titulo="Por tributo">
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                        {Object.entries(TRIBUTOS).map(([k, r]) => (
                          <Linha key={k} r={r} v={brl(ap.por_tributo?.[k] ?? 0)} />
                        ))}
                      </SimpleGrid>
                    </Secao>
                    <Secao titulo="Parcelas por anexo">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>Anexo</Th>
                            <Th isNumeric>Receita</Th>
                            <Th isNumeric>Faixa</Th>
                            <Th isNumeric>Alíq. efetiva</Th>
                            <Th isNumeric>Valor</Th>
                            <Th>Fora do DAS</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(det?.parcelas ?? []).map((p, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Tr key={i}>
                              <Td>{p.anexo}</Td>
                              <Td isNumeric>{brl(p.receita)}</Td>
                              <Td isNumeric>{p.faixa}ª</Td>
                              <Td isNumeric>{pct(p.aliquota_efetiva, 4)}</Td>
                              <Td isNumeric>{brl(p.valor)}</Td>
                              <Td>{p.fora_do_das.map(t => TRIBUTOS[t] ?? t).join(', ') || '—'}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Secao>
                    {!!det?.rbt12?.serie?.length && (
                      <Secao titulo="Receitas dos 12 meses anteriores">
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={1}>
                          {det.rbt12.serie.map(([m, v]) => (
                            <Linha key={m} r={dataBr(m).slice(3)} v={brl(v)} />
                          ))}
                        </SimpleGrid>
                      </Secao>
                    )}
                    <Secao titulo="Parâmetros da empresa">
                      <ParametrosResumo p={data.parametro} />
                    </Secao>
                  </Flex>
                </TabPanel>

                <TabPanel p={0}>
                  <Flex direction="column" gap={2}>
                    {data.pendencias.length === 0 && (
                      <Text fontSize="13px" color="#64748B">
                        Nenhum apontamento neste mês.
                      </Text>
                    )}
                    {data.pendencias.map(p => {
                      const n = NIVEL[p.nivel];
                      return (
                        <Box
                          key={p.id}
                          bg="white"
                          border="1px solid #E2E8F0"
                          borderLeft="4px solid"
                          borderLeftColor={`${n.cor}.400`}
                          borderRadius="12px"
                          p={3}
                          opacity={p.situacao === 'aberto' ? 1 : 0.7}
                        >
                          <Flex gap={2} align="center" wrap="wrap">
                            <Badge colorScheme={n.cor}>{n.rotulo}</Badge>
                            <Badge
                              variant="outline"
                              colorScheme={p.situacao === 'aberto' ? 'gray' : 'green'}
                            >
                              {p.situacao}
                            </Badge>
                            {p.documento && (
                              <Text fontSize="11px" color="#64748B">
                                {p.documento}
                              </Text>
                            )}
                          </Flex>
                          <Text fontSize="13px" color="#0F172A" mt={2}>
                            {p.mensagem}
                          </Text>
                          {p.situacao !== 'aberto' && (
                            <Text fontSize="11.5px" color="#64748B" mt={1}>
                              {p.justificativa}
                              {p.resolvido_por_nome ? ` — ${p.resolvido_por_nome}` : ''}
                              {p.resolvido_em ? `, ${dataHoraBr(p.resolvido_em)}` : ''}
                            </Text>
                          )}
                          {editavel && p.situacao === 'aberto' && p.nivel !== 'bloqueio' && (
                            <Flex gap={2} mt={2}>
                              <Input
                                size="sm"
                                placeholder="Justificativa para aceitar"
                                value={justificativas[p.id] ?? ''}
                                onChange={e =>
                                  setJustificativas(v => ({ ...v, [p.id]: e.target.value }))
                                }
                              />
                              <Button
                                size="sm"
                                colorScheme="green"
                                variant="outline"
                                isDisabled={(justificativas[p.id] ?? '').trim().length < 3}
                                isLoading={pendencia.isPending && pendencia.variables?.id === p.id}
                                onClick={() => pendencia.mutate({ id: p.id, acao: 'aceitar' })}
                              >
                                Aceitar
                              </Button>
                            </Flex>
                          )}
                          {editavel && p.situacao === 'aberto' && p.nivel === 'bloqueio' && (
                            <Text fontSize="11.5px" color="red.600" mt={2}>
                              Bloqueio não se aceita: corrija a causa (documento, parâmetro, RBT12)
                              e recalcule.
                            </Text>
                          )}
                          {editavel && p.situacao === 'aceito' && (
                            <Button
                              size="xs"
                              variant="ghost"
                              mt={1}
                              onClick={() => pendencia.mutate({ id: p.id, acao: 'reabrir' })}
                            >
                              Reabrir
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Flex>
                </TabPanel>

                <TabPanel p={0}>
                  <Input
                    size="sm"
                    bg="white"
                    placeholder="Filtrar documento"
                    value={filtroDoc}
                    onChange={e => setFiltroDoc(e.target.value)}
                    mb={2}
                  />
                  {data.correcoes.length > 0 && (
                    <Secao titulo={`Correções manuais (${data.correcoes.length})`}>
                      {data.correcoes.map(c => (
                        <Flex key={c.id} align="center" gap={2} py={1} fontSize="12px">
                          <Text flex={1} noOfLines={1}>
                            <b>{c.documento}</b> · {c.ignorar ? 'ignorado' : 'reclassificado'} ·{' '}
                            {c.motivo}
                            {c.usuario_nome ? ` (${c.usuario_nome})` : ''}
                          </Text>
                          {editavel && (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              leftIcon={<Trash size={12} />}
                              isLoading={
                                excluirCorrecao.isPending && excluirCorrecao.variables === c.id
                              }
                              onClick={() => excluirCorrecao.mutate(c.id as number)}
                            >
                              Remover
                            </Button>
                          )}
                        </Flex>
                      ))}
                    </Secao>
                  )}
                  <Box
                    bg="white"
                    border="1px solid #E2E8F0"
                    borderRadius="14px"
                    mt={2}
                    overflowX="auto"
                  >
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Documento</Th>
                          <Th isNumeric>Anexo</Th>
                          <Th isNumeric>Valor</Th>
                          <Th>Fora do DAS</Th>
                          <Th />
                        </Tr>
                      </Thead>
                      <Tbody>
                        {linhas.map((l, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Tr key={`${l.documento}-${i}`}>
                            <Td fontSize="12px" maxW="260px">
                              <Text noOfLines={1} title={l.documento}>
                                {l.documento}
                              </Text>
                              {correcaoPorDoc[l.documento] && (
                                <Badge colorScheme="purple" fontSize="9px">
                                  corrigido
                                </Badge>
                              )}
                            </Td>
                            <Td isNumeric>{l.anexo}</Td>
                            <Td isNumeric>{brl(l.valor)}</Td>
                            <Td fontSize="12px">
                              {l.fora_do_das.map(t => TRIBUTOS[t] ?? t).join(', ') || '—'}
                            </Td>
                            <Td>
                              {editavel && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  leftIcon={<PencilSimple size={12} />}
                                  onClick={() => setCorrigindo(l)}
                                >
                                  Corrigir
                                </Button>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  {!editavel && (
                    <Text fontSize="11.5px" color="#64748B" mt={2}>
                      Correções só com a apuração em conferência (desfaça a aprovação; mês
                      transmitido só por retificação).
                    </Text>
                  )}
                </TabPanel>

                <TabPanel p={0}>
                  {data.arquivos.length === 0 ? (
                    <Text fontSize="13px" color="#64748B">
                      Nenhum arquivo ainda: declaração e recibo saem na transmissão; DAS e extrato
                      em seguida.
                    </Text>
                  ) : (
                    <Flex direction="column" gap={2}>
                      {data.arquivos.map(a => (
                        <Flex
                          key={a.id}
                          align="center"
                          gap={3}
                          bg="white"
                          border="1px solid #E2E8F0"
                          borderRadius="12px"
                          p={3}
                        >
                          <Box flex={1}>
                            <Text fontSize="13px" fontWeight={600}>
                              {TIPOS_ARQUIVO[a.tipo] ?? a.tipo}
                              {a.numero ? ` · ${a.numero}` : ''}
                            </Text>
                            <Text fontSize="11px" color="#64748B">
                              {dataHoraBr(a.dt_criacao)}
                            </Text>
                          </Box>
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<DownloadSimple size={14} />}
                            isLoading={baixar.isPending && baixar.variables === a.id}
                            onClick={() => baixar.mutate(a.id)}
                          >
                            Abrir
                          </Button>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </DrawerBody>
      </DrawerContent>
      {corrigindo && (
        <CorrecaoModal
          linha={corrigindo}
          atual={correcaoPorDoc[corrigindo.documento]}
          onFechar={() => setCorrigindo(null)}
          onSalvar={c => salvarCorrecao.mutate(c)}
          salvando={salvarCorrecao.isPending}
        />
      )}
      {retificando && ap && data && (
        <RetificarModal
          ap={ap}
          cnpj={data.empresa.cnpj ?? ''}
          onFechar={() => setRetificando(false)}
          onFeito={() => {
            setRetificando(false);
            atualizar();
          }}
        />
      )}
    </Drawer>
  );
}
